// ---------------------------------------------------------------------------
// Design tokens — single source of truth for visual constants.
//
// Two-layer token architecture:
//   1. Primitive tokens: raw values (hex colours, pixel sizes, ms durations).
//   2. Semantic tokens: named by MEANING, resolved against the active palette.
//      Semantic tokens are the only thing screens should reference. They survive
//      palette changes and brand swaps without touching screen code.
//
// All tokens are plain const exports, so they tree-shake and cost nothing at
// runtime. Theme-dependent semantic tokens are returned by helper fns that take
// the resolved mode, so they stay computed inside `createCadenceTheme` and do
// not force component re-renders.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Primitive: spacing — 4px base unit, named for intent not pixel count.
// (MUI's `theme.spacing(n)` remains available for grid maths.)
// ---------------------------------------------------------------------------

export const space = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
} as const;
export type SpaceToken = keyof typeof space;

// ---------------------------------------------------------------------------
// Primitive: type scale — kept deliberately small per UX recommendation §3.2
// ("two weights, no more than three sizes per screen"). Screens should pick
// three sizes from this set, not mint new ones.
// ---------------------------------------------------------------------------

export const text = {
  xs: 12,
  sm: 13,
  base: 15, // matches §7.3 minimum body size
  lg: 17,
  xl: 20,
  xxl: 24,
  display: 32,
} as const;
export type TextToken = keyof typeof text;

export const weight = {
  regular: 400,
  semibold: 600,
  bold: 700,
} as const;
export type WeightToken = keyof typeof weight;

export const lineHeight = {
  tight: 1.3,
  body: 1.5,
  long: 1.7, // transcripts, reflections — §7.3
} as const;

// ---------------------------------------------------------------------------
// Primitive: radii — MUI `shape.borderRadius` already sets base. These give
// named steps for components that deviate.
// ---------------------------------------------------------------------------

export const radii = {
  none: 0,
  sm: 6,
  md: 10,
  lg: 14,
  pill: 999,
} as const;
export type RadiiToken = keyof typeof radii;

// ---------------------------------------------------------------------------
// Primitive: motion — every duration capped at 200ms per §7.4.
// Any animation longer than `standard` is a bug.
// ---------------------------------------------------------------------------

export const motion = {
  instant: 0,
  fast: 120,
  standard: 200,
} as const;
export type MotionToken = keyof typeof motion;

export const easing = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  enter: 'cubic-bezier(0, 0, 0, 1)',
  exit: 'cubic-bezier(0.3, 0, 1, 1)',
} as const;

// ---------------------------------------------------------------------------
// Primitive: z-index layers — named so components stop inventing z-index
// values. Keep this list short; if you need a new layer there is probably a
// reparenting or portal opportunity.
// ---------------------------------------------------------------------------

export const zIndex = {
  base: 0,
  sticky: 100,
  overlay: 1000,
  drawer: 1100,
  modal: 1300,
  popover: 1400,
  toast: 1500,
} as const;

// ---------------------------------------------------------------------------
// Semantic: status colours — the only "meaning" colours allowed per §3.3.
// Green for on-plan, amber for at-risk, red for breached. Never decorative.
//
// Each mode gets its own mapping so contrast stays WCAG AA in both themes.
// ---------------------------------------------------------------------------

export interface StatusColours {
  onPlan: string;
  atRisk: string;
  breach: string;
  unaligned: string; // for work items with no driver link
}

const statusDark: StatusColours = {
  onPlan: '#69F0AE', // green
  atRisk: '#FFB74D', // amber
  breach: '#FF5252', // red
  unaligned: '#8892A8', // muted neutral — NOT a status colour
};

const statusLight: StatusColours = {
  onPlan: '#2E7D32',
  atRisk: '#ED6C02',
  breach: '#D32F2F',
  unaligned: '#5A6478',
};

const statusHighContrastDark: StatusColours = {
  onPlan: '#7DFFBE',
  atRisk: '#FFC857',
  breach: '#FF6B6B',
  unaligned: '#B0B8CC',
};

const statusHighContrastLight: StatusColours = {
  onPlan: '#1B5E20',
  atRisk: '#BF360C',
  breach: '#B71C1C',
  unaligned: '#1A1A1A',
};

export function getStatusColours(
  mode: 'dark' | 'light',
  highContrast: boolean,
): StatusColours {
  if (highContrast) {
    return mode === 'dark' ? statusHighContrastDark : statusHighContrastLight;
  }
  return mode === 'dark' ? statusDark : statusLight;
}

// ---------------------------------------------------------------------------
// Semantic: surface — naming for background layers so components stop
// hard-coding paper colours. Currently thin — extend as screens are rebuilt.
// ---------------------------------------------------------------------------

export interface SurfaceColours {
  page: string;
  card: string;
  raised: string;
  subtle: string; // for muted rows, disabled panels
  border: string;
  borderStrong: string;
}

export function getSurfaceColours(
  mode: 'dark' | 'light',
  highContrast: boolean,
): SurfaceColours {
  if (mode === 'dark') {
    // Realigned to the logo slate (#1D2939) so brand mark, UI and
    // marketing share one palette. Page sits slightly darker than the
    // card so the layered surfaces still read as distinct depths.
    return {
      page: '#131A24',
      card: '#1D2939',
      raised: '#2A3848',
      subtle: '#182230',
      border: highContrast ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)',
      borderStrong: highContrast ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)',
    };
  }
  return {
    page: '#FAFBFD',
    card: '#FFFFFF',
    raised: '#FFFFFF',
    subtle: '#F3F5F9',
    border: highContrast ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.06)',
    borderStrong: highContrast ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.12)',
  };
}

// ---------------------------------------------------------------------------
// Semantic: driver tag palette — deterministic colour-per-driver so the same
// driver always looks the same across dashboard, calendar, projects, reports.
// Use `colourForDriverId(id)` to pick; never hard-code.
//
// Chosen to be AA-legible on both dark surface (#121829) and light (#FFFFFF)
// with white/near-white text. Ordered for max perceptual separation in small
// dot badges.
// ---------------------------------------------------------------------------

export const driverPalette = [
  '#6C9BFF', // blue
  '#B39DFF', // lavender
  '#FF8A65', // coral
  '#4FD1C5', // teal
  '#F6AD55', // tangerine
  '#F687B3', // pink
  '#9AE6B4', // mint
  '#FBD38D', // straw
] as const;

export function colourForDriverId(id: string | number | null | undefined): string {
  if (id === null || id === undefined) return '#8892A8'; // unaligned
  // Simple deterministic hash — string to int.
  const key = String(id);
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return driverPalette[Math.abs(hash) % driverPalette.length];
}

// ---------------------------------------------------------------------------
// Calendar Calm / Minimal colour modes — neutral tile + half-chroma driver
// hues. For an exec orchestration surface, colour is earned by meaning
// (protected / at-risk / clash), not spent on which driver a meeting belongs
// to (the title + a small dot already convey that). Dark-mode values; add
// light-mode equivalents alongside if/when light mode is reviewed.
// ---------------------------------------------------------------------------

export const calendarCalm = {
  tileBgDark: '#243248',
  tileFgDark: '#E3E9F4',
  edgeMeeting: '#33445F',   // quiet, almost invisible
  edgePrivate: '#C56B6B',
  edgeAllDay:  '#C79A5A',
  edgeClash:   '#FF5252',   // the only vivid accent that survives into Calm/Minimal
} as const;

// Muted parallel to driverPalette (same order), ~50% chroma.
export const driverPaletteMuted = [
  '#5F7BA6', // blue
  '#8D84AD', // lavender
  '#BD7F63', // coral
  '#4F8F89', // teal
  '#C0975A', // tangerine
  '#AB7589', // pink
  '#6F9E82', // mint
  '#B8A268', // straw
] as const;

// Keep this hashing IDENTICAL to colourForDriverId so a driver's Calm hue is
// the muted twin of its Categorical hue.
export function mutedColourForDriverId(id: string | number | null | undefined): string {
  if (id == null) return '#5F7BA6';
  const key = String(id);
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return driverPaletteMuted[Math.abs(hash) % driverPaletteMuted.length];
}

// ---------------------------------------------------------------------------
// Focus ring — one shared definition, applied globally via MuiCssBaseline
// (see theme.ts) so every :focus-visible element gets the same treatment
// without each component declaring its own.
// ---------------------------------------------------------------------------

export const focusRing = {
  width: 2,
  offset: 2,
  // Colour is resolved at render-time via `currentColor` / palette.primary.main
  // so the ring matches the active accent in dark / light / dyslexia modes.
} as const;
